import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import type {
  ClusterDetailResponse,
  ClusterImpact,
  ClusterMaterialImpact,
  TradingCluster,
} from "../api/types";
import { clockTime, percent, relativeTime } from "../components/format";
import { severityForScore } from "./severity";
import type { SeverityThresholds } from "./types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SeverityBadge } from "@/components/trading/SeverityBadge";
import { EnrichmentBadges } from "@/components/trading/EnrichmentBadges";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { NotesBadge } from "@/notes/NotesBadge";
import { AddNoteButton } from "@/notes/AddNoteButton";
import { AskAboutButton } from "@/copilot/AskAboutButton";
import { cn } from "@/lib/utils";

interface ClusterCardProps {
  cluster: TradingCluster;
  variant: "fresh" | "consumed" | "not-in-run";
  expanded: boolean;
  selected: boolean;
  materialId: string;
  otherMaterialId: string;
  thresholds: SeverityThresholds;
  now: number;
  driverWeight?: number;
  highlighted?: boolean;
  notesCount?: number;
  onToggle: (clusterId: string) => void;
  onOpenArticle: (articleId: string, siblingIds: string[]) => void;
  onJumpToOtherPane: (clusterId: string, materialId: string) => void;
  onOpenNotes?: () => void;
}

// `now` is intentionally accepted but unused here — relativeTime uses
// Date.now() internally, which is what tests + the live UI both want.
// The signature stays compatible so callers don't need to update.
function ageLabel(iso: string, _now: number): string {
  return relativeTime(iso);
}

function impactFromDetail(
  detail: ClusterDetailResponse | undefined,
  materialId: string,
): ClusterMaterialImpact | undefined {
  if (!detail) return undefined;
  return detail.material_impacts.find((impact) => impact.material_id === materialId);
}

function ImpactDl({
  impact,
}: {
  impact: ClusterImpact | ClusterMaterialImpact | undefined;
}) {
  const { t } = useTranslation("dashboard");
  if (!impact)
    return (
      <p className="text-sm text-muted-foreground">{t("cluster.noImpact")}</p>
    );

  return (
    <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-sm">
      <Tooltip>
        <TooltipTrigger asChild>
          <dt className="text-muted-foreground cursor-help">
            {t("cluster.impactChannel.label")}
          </dt>
        </TooltipTrigger>
        <TooltipContent>{t("cluster.impactChannel.help")}</TooltipContent>
      </Tooltip>
      <dd className="font-mono">{impact.impact_channel}</dd>

      <Tooltip>
        <TooltipTrigger asChild>
          <dt className="text-muted-foreground cursor-help">
            {t("cluster.direction.label")}
          </dt>
        </TooltipTrigger>
        <TooltipContent>{t("cluster.direction.help")}</TooltipContent>
      </Tooltip>
      <dd>{impact.direction_hint ?? t("cluster.direction.pending")}</dd>

      <Tooltip>
        <TooltipTrigger asChild>
          <dt className="text-muted-foreground cursor-help">
            {t("cluster.horizon.label")}
          </dt>
        </TooltipTrigger>
        <TooltipContent>{t("cluster.horizon.help")}</TooltipContent>
      </Tooltip>
      <dd>{impact.horizon_hint ?? t("cluster.horizon.pending")}</dd>

      <Tooltip>
        <TooltipTrigger asChild>
          <dt className="text-muted-foreground cursor-help">
            {t("cluster.relevance.label")}
          </dt>
        </TooltipTrigger>
        <TooltipContent>{t("cluster.relevance.help")}</TooltipContent>
      </Tooltip>
      <dd className="font-mono">{percent(impact.relevance_score)}</dd>

      <dt className="text-muted-foreground col-span-2 mt-2">
        {t("cluster.reason")}
      </dt>
      <dd className="col-span-2 text-sm">{impact.reason}</dd>
    </dl>
  );
}

export function ClusterCard({
  cluster,
  variant,
  expanded,
  selected,
  materialId,
  otherMaterialId,
  thresholds,
  now,
  driverWeight,
  highlighted = false,
  notesCount = 0,
  onToggle,
  onOpenArticle,
  onJumpToOtherPane,
  onOpenNotes,
}: ClusterCardProps) {
  const { t } = useTranslation("dashboard");
  const detailQuery = useQuery({
    queryKey: ["cluster", cluster.cluster_id],
    queryFn: () => api.getCluster(cluster.cluster_id),
    enabled: expanded,
  });
  const detail = detailQuery.data;
  const score = cluster.impact?.relevance_score;
  const severity = severityForScore(score, thresholds);
  const sampleWires = cluster.wire_origins_sample?.filter(Boolean).slice(0, 3) ?? [];
  const siblingIds = detail?.members.map((member) => member.article_id) ?? [];
  const detailImpact = impactFromDetail(detail, materialId);
  const alsoInOtherPane = cluster.also_impacts_materials?.includes(otherMaterialId);

  return (
    <Card
      className={cn(
        "py-0 gap-0 transition-all",
        selected && "ring-2 ring-primary",
        highlighted && "ring-2 ring-amber-500",
        variant === "consumed" && "border-primary/30",
        variant === "not-in-run" && "opacity-70",
      )}
      data-cluster-id={cluster.cluster_id}
      data-tape-id={cluster.cluster_id}
      aria-expanded={expanded}
    >
      <button
        className="w-full text-start p-3 flex flex-col gap-1.5"
        onClick={() => onToggle(cluster.cluster_id)}
        type="button"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <SeverityBadge severity={severity} />
          <Badge variant="secondary" className="text-xs">{cluster.primary_category}</Badge>
          <EnrichmentBadges enrichment={cluster.enrichment} />
          <span className="text-xs text-muted-foreground">{ageLabel(cluster.first_seen_at, now)}</span>
          <span className="text-xs text-muted-foreground">
            {t("cluster.wireCount", { count: cluster.distinct_wire_origins })}
          </span>
          {onOpenNotes != null ? (
            <span
              className="ms-auto flex items-center gap-1"
              onClick={(e) => { e.stopPropagation(); }}
            >
              <NotesBadge count={notesCount} onClick={onOpenNotes} />
              <AddNoteButton onClick={onOpenNotes} />
              <AskAboutButton
                target={{
                  kind: "cluster",
                  id: cluster.cluster_id,
                  title: cluster.representative_title,
                }}
              />
            </span>
          ) : null}
          {variant === "not-in-run" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs cursor-help">
                  {t("cluster.variant.notInRun.label")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("cluster.variant.notInRun.help")}</TooltipContent>
            </Tooltip>
          ) : null}
          {variant === "consumed" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs cursor-help">
                  {t("cluster.variant.consumed.label")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("cluster.variant.consumed.help")}</TooltipContent>
            </Tooltip>
          ) : null}
          {variant === "fresh" ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs cursor-help">
                  {t("cluster.variant.fresh.label")}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("cluster.variant.fresh.help")}</TooltipContent>
            </Tooltip>
          ) : null}
          {driverWeight != null ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="secondary" className="text-xs font-mono cursor-help">
                  {t("cluster.driverBadge", { weight: percent(driverWeight) })}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{t("cluster.driverHelp")}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        {/* M12: publisher-supplied titles are arbitrary text from upstream
            feeds — wrap in <bdi> so any latent bidi-override codepoints
            cannot reorder surrounding UI chrome in Arabic context. */}
        <strong className="text-sm"><bdi>{cluster.representative_title}</bdi></strong>
        <div className="text-xs text-muted-foreground">
          {cluster.impact?.impact_channel ?? t("cluster.channel.pending")} ·{" "}
          {cluster.impact?.direction_hint ?? t("cluster.direction.pending")} ·{" "}
          {t("cluster.relevance.inline", { value: percent(score ?? 0) })}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {sampleWires.length > 0
            ? sampleWires.join(", ")
            : t("cluster.wires.pending")}
        </div>
      </button>

      {alsoInOtherPane ? (
        <div className="px-3 pb-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => onJumpToOtherPane(cluster.cluster_id, otherMaterialId)}
            className="h-auto p-0"
          >
            {t("cluster.alsoIn", { material: otherMaterialId })}
          </Button>
        </div>
      ) : null}

      {expanded ? (
        <CardContent className="px-3 pb-3 pt-0 space-y-3">
          {detailQuery.isPending ? (
            <div className="text-sm text-muted-foreground">
              {t("cluster.loadingDetail")}
            </div>
          ) : null}
          {detailQuery.isError ? (
            <div className="text-sm text-destructive">{(detailQuery.error as Error).message}</div>
          ) : null}
          {detail ? (
            <>
              <ImpactDl impact={detailImpact ?? cluster.impact} />

              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5">
                  {t("cluster.categories")}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {detail.categories.map((category) => (
                    <Badge variant="secondary" className="text-xs" key={category.category_id}>
                      {category.category_id} {percent(category.score)}
                      {category.is_primary ? ` ${t("cluster.primary")}` : ""}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5">
                  {t("cluster.members")}
                </div>
                <div className="flex flex-col gap-1">
                  {detail.members.map((member) => (
                    <button
                      className="flex items-center gap-2 p-1.5 text-sm text-start rounded hover:bg-muted"
                      key={member.article_id}
                      onClick={() => onOpenArticle(member.article_id, siblingIds)}
                      type="button"
                    >
                      <span className="text-xs font-mono text-muted-foreground">{clockTime(member.joined_at)}</span>
                      <code className="text-xs text-muted-foreground">
                        {member.feed_id ?? t("cluster.feedPending")}
                      </code>
                      {/* M12: member titles are publisher-supplied — isolate. */}
                      <strong className="flex-1 truncate">
                        {member.title ? <bdi>{member.title}</bdi> : member.article_id}
                      </strong>
                      {member.is_representative || member.article_id === detail.representative_article_id ? (
                        <Badge variant="secondary" className="text-xs">
                          {t("cluster.representative")}
                        </Badge>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                {t("cluster.distinctWires", {
                  count: detail.stats.distinct_wire_origins,
                })}
                {detail.stats.wire_origins.length > 0
                  ? `: ${detail.stats.wire_origins.join(", ")}`
                  : ""}
              </div>
            </>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}
